import { capitalize } from 'lodash';
import { DataFieldConfig } from './data-builder';
import { ICONS } from '~/constants/icons';
import { NestedHome } from '@habity/types';
import { CommonEstateRoom } from '@habity/types/vitec/common/commonEstateRoom';
import {
  isCondominium,
  isCottage,
  isFarm,
  isHouse,
  isHousingCooperative,
} from '@habity/types/vitec/assertions';

const getRoomIcon = (heading: string) => {
  if (heading.toLowerCase().includes('kök'))
    return ICONS.ROOM_DESCRIPTIONS.KITCHEN;
  if (heading.toLowerCase().includes('vardagsrum'))
    return ICONS.ROOM_DESCRIPTIONS.LIVING_ROOM;
  if (heading.toLowerCase().includes('sovrum'))
    return ICONS.ROOM_DESCRIPTIONS.BEDROOM;
  if (heading.toLowerCase().includes('badrum'))
    return ICONS.ROOM_DESCRIPTIONS.BATHROOM;
  if (heading.toLowerCase().includes('tvättstuga'))
    return ICONS.ROOM_DESCRIPTIONS.LAUNDRY_ROOM;
  if (heading.toLowerCase().includes('hall'))
    return ICONS.ROOM_DESCRIPTIONS.HALLWAY;
  if (heading.toLowerCase().includes('förråd'))
    return ICONS.ROOM_DESCRIPTIONS.STORAGE;
  if (heading.toLowerCase().includes('kontor'))
    return ICONS.ROOM_DESCRIPTIONS.OFFICE;
  if (heading.toLowerCase().includes('allrum'))
    return ICONS.ROOM_DESCRIPTIONS.LIVING_ROOM;
  if (heading.toLowerCase().includes('gästrum'))
    return ICONS.ROOM_DESCRIPTIONS.BEDROOM;
  if (heading.toLowerCase().includes('bibliotek'))
    return ICONS.ROOM_DESCRIPTIONS.LIBRARY;
  if (heading.toLowerCase().includes('matrum'))
    return ICONS.ROOM_DESCRIPTIONS.DINING_ROOM;
  if (heading.toLowerCase().includes('bastu'))
    return ICONS.ROOM_DESCRIPTIONS.SAUNA;
  if (heading.toLowerCase().includes('sällskapsrum'))
    return ICONS.ROOM_DESCRIPTIONS.SOCIAL_ROOM;
  if (heading.toLowerCase().includes('sal'))
    return ICONS.ROOM_DESCRIPTIONS.DINING_HALL;
  if (heading.toLowerCase().includes('wc'))
    return ICONS.ROOM_DESCRIPTIONS.TOILET;
  if (heading.toLowerCase().includes('bar')) return ICONS.ROOM_DESCRIPTIONS.BAR;
  if (heading.toLowerCase().includes('trädgård'))
    return ICONS.ROOM_DESCRIPTIONS.GARDEN;
  if (
    heading.toLowerCase().includes('uteplats') ||
    heading.toLowerCase().includes('terrass') ||
    heading.toLowerCase().includes('altan') ||
    heading.toLowerCase().includes('veranda')
  )
    return ICONS.ROOM_DESCRIPTIONS.TERRACE;
  if (heading.toLowerCase().includes('klädkammare'))
    return ICONS.ROOM_DESCRIPTIONS.WALK_IN_CLOSET;
  if (heading.toLowerCase().includes('uppfart'))
    return ICONS.ROOM_DESCRIPTIONS.CARPORT;
  if (heading.toLowerCase().includes('entre'))
    return ICONS.ROOM_DESCRIPTIONS.ENTRANCE;
  if (heading.toLowerCase().includes('renoveringar'))
    return ICONS.ROOM_DESCRIPTIONS.RENOVATIONS;
  return ICONS.ROOM_DESCRIPTIONS.OTHER;
};

export const getRoomsFromHome = (context: { home: NestedHome }) => {
  const { home } = context;
  const { provider_data, provider_type } = home || {};
  if (!provider_data || !provider_type) return [];

  if (
    isHouse(provider_data, provider_type) ||
    isCottage(provider_data, provider_type)
  ) {
    // @ts-ignore
    const { houseInterior } = provider_data || {};
    const { rooms } = houseInterior || {};
    return rooms;
  }

  if (
    isHousingCooperative(provider_data, provider_type) ||
    isCondominium(provider_data, provider_type)
  ) {
    const { interior } = provider_data || {};
    const { rooms } = interior || {};
    return rooms;
  }

  if (isFarm(provider_data, provider_type)) {
    const { mainResidentialBuilding } = provider_data || {};
    const { rooms } = mainResidentialBuilding || {};
    return rooms;
  }

  return [];
};

// Create dynamic fields based on rooms data
export const createRoomsFields = (context: {
  home: NestedHome;
}): DataFieldConfig[] => {
  const rooms = getRoomsFromHome(context);

  if (!rooms || rooms.length === 0) return [];

  // If rooms is an array of room objects with heading and text
  if (
    Array.isArray(rooms) &&
    rooms.length > 0 &&
    typeof rooms[0] === 'object' &&
    rooms[0].heading
  ) {
    return rooms.map((room: CommonEstateRoom, index: number) => ({
      key: `room_${index}`,
      label: capitalize(room.heading),
      icon: getRoomIcon(room.heading || ''),
      getValue: () => room.text,
      condition: () => true, // Always include if room exists
    }));
  }

  // If rooms is a simple value (fallback)
  return [
    {
      key: 'rooms_fallback',
      label: 'Rum',
      icon: getRoomIcon(''),
      getValue: () => rooms?.toString() || '',
      condition: () => true,
    },
  ];
};
